import { AbcCore, ConvertHelper } from '@barthauer/abc/core';
import {
  ICoreDataEntity,
  Query,
  QueryObjectsModel,
  PersistObjectModel,
  PersistObjectsModel,
  TargetObjectData,
  PersistMode,
  TargetColumnValue,
  ObjectKey,
  QueryHelper,
} from '@barthauer/abc/core/data';
import { ParameterValue } from '@barthauer/abc/queries/parameters/core';
import { TicketNotificationData } from '../viewModels/tickets.viewModel';
import { Injectable } from '@angular/core';

const Filters = {
  MtTicketTask_byUser:
    'Exists(Resource[Login=CaseNull(CurrentSystemUserName(), CurrentOSUserName()) Or IIF(IsNull(CurrentSystemUserName()), IsCurrentOSUserInGroup(Login), IsCurrentSystemUserInGroup(Login))])',
  Reminder_isActive: 'IsActive And IsNull(IdRefPersonFor)',
  Reminder_notIsActive: 'IsActive<>true And IsNull(IdRefPersonFor)',
};
export const OT = {
  MtObjWorkType: { name: 'MtObjWorkType' },
  Auftrag: { name: 'Auftrag' },
  MtTicket: {
    name: 'MtTicket',
    filters: {
      withActiveReminers: `Exists(TicketTasks[${Filters.MtTicketTask_byUser}].Reminders[${Filters.Reminder_isActive}])`,
    },
  },
  MtTicketTask: {
    name: 'MtTicketTask',
    filters: {
      byUser: Filters.MtTicketTask_byUser,
    },
  },
  Reminder: {
    name: 'Reminder',
    filters: {
      isActive: Filters.Reminder_isActive,
      notIsActive: Filters.Reminder_notIsActive,
    },
  },
  MtWork: {
    name: 'MtWork',
  },
};

@Injectable()
export class DataService {
  constructor(private core: AbcCore) {}

  public async insertOrUpdateReminder(parentObjectType: string, parentId: string): Promise<void> {
    const persistObjectModel: PersistObjectModel = new PersistObjectModel();
    const newObj: TargetObjectData = new TargetObjectData();
    newObj.ObjectKey = new ObjectKey();
    newObj.ObjectKey.ObjectType = OT.Reminder.name;
    newObj.Mode = PersistMode.Merge;

    const now: Date = new Date();
    const targetColumns: TargetColumnValue[] = new Array<TargetColumnValue>();
    targetColumns.push(this.core.data.createPersistTargetColumn('ParentObjectType', parentObjectType));
    targetColumns.push(this.core.data.createPersistTargetColumn('ParentId', parentId));
    targetColumns.push(this.core.data.createPersistTargetColumn('IsActive', true));
    targetColumns.push(this.core.data.createPersistTargetColumn('Date', ConvertHelper.toISODateString(now)));
    targetColumns.push(this.core.data.createPersistTargetColumn('Time', ConvertHelper.toISOTimeString(now)));

    newObj.TargetColumns = targetColumns;
    persistObjectModel.Object = newObj;

    await this.core.data.executePersistObjectQuery(persistObjectModel).toPromise();
  }

  public async setRemindersActive(entity: ICoreDataEntity, isActive: boolean): Promise<void> {
    const query: QueryObjectsModel = {
      Parameters: [{ Name: 'TicketId', Value: entity?.Id }],
      ObjectQueries: [
        {
          ObjectType: OT.Reminder.name,
          OPath: QueryHelper.combineFilters([
            isActive ? OT.Reminder.filters.notIsActive : OT.Reminder.filters.isActive,
            `Exists(!.Cast(${OT.MtTicketTask.name})[${QueryHelper.combineFilters(['ParentId=$TicketId', OT.MtTicketTask.filters.byUser])}])`,
          ]),
          Columns: [
            {
              Name: 'Id',
              OPath: 'Id',
            },
          ],
        },
      ],
    };
    const objects = ConvertHelper.arrayBufferToObjectArray<{ Id: string }>(
      await this.core.data.executeReadObjectsQuery(query).toPromise(),
    );
    if (objects) {
      const persist: PersistObjectsModel = {
        Objects: [
          objects.map((o) => ({
            ObjectKey: { ObjectType: OT.Reminder.name, Id: o.Id },
            Mode: PersistMode.Update,
            TargetColumns: [
              {
                Name: 'IsActive',
                Value: isActive,
              },
            ],
          })),
        ],
      };

      await this.core.data.executePersistObjectsQuery(persist).toPromise();
    }
  }

  public async queryTicketNotificationData(taskId: string): Promise<TicketNotificationData> {
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();

    const mainQuery: Query = new Query();
    mainQuery.ObjectType = OT.MtTicketTask.name;
    mainQuery.OPath = `Id=$Id`;
    mainQuery.Columns = [
      this.core.data.createQueryColumn('Ticket.Nummer', 'TicketNumber'),
      this.core.data.createQueryColumn('Ticket.Bezeichnung', 'TicketDesignation'),
      this.core.data.createQueryColumn('ref:Ticket.Dringlichkeit', 'TicketPriority'),
      this.core.data.createQueryColumn('Bezeichnung', 'TicketTaskDesignation'),
      this.core.data.createQueryColumn('Resource.Login', 'TicketTaskResourceLogin'),
    ];
    queryDocument.ObjectQueries = [mainQuery];

    queryDocument.Parameters = [new ParameterValue('Id', taskId)];

    const result = ConvertHelper.arrayBufferToObjectArray(
      await this.core.data.executeReadObjectsQuery(queryDocument, false).toPromise(),
    );
    return result.map((obj) => new TicketNotificationData(obj))[0];
  }
}
